import React, { useState } from 'react'
import { ResponsiveContainer, PieChart, Pie, Tooltip, Cell } from 'recharts'

export default function MixRatios({
	stopMix = [],
	teamMix = [],
	distanceMix = [],
	isDarkMode,
}) {
	const lightPalette = {
		solo: '#4F8EF7',
		team: '#5A67D8',
		one: '#4F8EF7',
		two: '#5A67D8',
		threePlus: '#7C3AED',
		short: '#4F8EF7',
		mid: '#5A67D8',
		long: '#7C3AED',
		twoStops: '#4F8EF7',
		threeStops: '#5A67D8',
		fourFiveStops: '#7C3AED',
		sixSevenStops: '#9333EA',
		eightTenStops: '#C026D3',
		axis: '#6B7280',
		cardBg: '#ffffff',
		border: '#cccccc',
		// distance palettes
		dist1: '#4F8EF7',
		dist2: '#5A67D8',
		dist3: '#7C3AED',
		dist4: '#9333EA',
		dist5: '#C026D3',
	}

	const darkPalette = {
		solo: '#60A5FA',
		team: '#818CF8',
		one: '#60A5FA',
		two: '#818CF8',
		threePlus: '#A78BFA',
		short: '#60A5FA',
		mid: '#818CF8',
		long: '#A78BFA',
		twoStops: '#60A5FA',
		threeStops: '#818CF8',
		fourFiveStops: '#A78BFA',
		sixSevenStops: '#C4B5FD',
		eightTenStops: '#E9D5FF',
		axis: '#D1D5DB',
		cardBg: '#2d333b',
		border: '#444c56',
		// distance palettes
		dist1: '#60A5FA',
		dist2: '#818CF8',
		dist3: '#A78BFA',
		dist4: '#C4B5FD',
		dist5: '#E9D5FF',
	}

	const P = isDarkMode ? darkPalette : lightPalette

	const getBarFill = key => {
		// Colors adjusted for dark/light mode
		return P[key] || P.axis
	}

	// --- Dummy datasets for tab views ---
	const buildTeamSolo = () => {
		if (!Array.isArray(teamMix) || !teamMix.length) return null
		const total = teamMix.reduce((s, b) => s + (b.count || 0), 0) || 1
		return teamMix.map(b => ({
			name: b.driver,
			key: b.driver.toLowerCase(),
			value: +((b.count / total) * 100).toFixed(1),
			loads: b.count,
			revenue: b.revenue || 0,
			rpm: b.avgRpm || 0,
		}))
	}

	const teamSolo = buildTeamSolo() || [
		{
			name: 'Solo',
			key: 'solo',
			value: 65,
			loads: 6500,
			revenue: 26000,
			rpm: 2.1,
		},
		{
			name: 'Team',
			key: 'team',
			value: 35,
			loads: 3500,
			revenue: 21000,
			rpm: 3.4,
		},
	]

	// build distance dataset
	const buildDistance = () => {
		if (!Array.isArray(distanceMix) || !distanceMix.length) return null
		const total = distanceMix.reduce((s, b) => s + (b.count || 0), 0) || 1
		const mapKey = bucket => {
			if (bucket.startsWith('0-')) return 'dist1'
			if (bucket.startsWith('500-')) return 'dist2' // 500-1000
			if (bucket.startsWith('1000-')) return 'dist3'
			if (bucket.startsWith('1500-')) return 'dist4'
			return 'dist5'
		}
		return distanceMix.map(b => ({
			name: b.bucket,
			key: mapKey(b.bucket),
			value: +((b.count / total) * 100).toFixed(1),
			loads: b.count,
			revenue: b.revenue || 0,
			rpm: b.avgRpm || 0,
		}))
	}

	const tripDistance = buildDistance() || []

	// Build stopCount dataset from stopMix prop if provided
	const buildStopCount = () => {
		if (!Array.isArray(stopMix) || !stopMix.length) return null
		const total = stopMix.reduce((s, b) => s + (b.count || 0), 0) || 1
		const mapKey = bucket => {
			const str = String(bucket || '')
			if (str.startsWith('2')) return 'twoStops'
			if (str.startsWith('3')) return 'threeStops'
			if (str.startsWith('4')) return 'fourFiveStops'
			if (str.startsWith('6')) return 'sixSevenStops'
			return 'eightTenStops'
		}
		return stopMix.map(b => ({
			name: b.bucket,
			key: mapKey(b.bucket),
			value: +((b.count / total) * 100).toFixed(1),
			loads: b.count,
			revenue: b.revenue || 0,
			rpm: b.avgRpm || 0,
		}))
	}

	const stopCount = buildStopCount() || [
		// fallback dummy
		{
			name: '2 Stops',
			key: 'twoStops',
			value: 35,
			loads: 3500,
			revenue: 15750,
			rpm: 4.5,
		},
		{
			name: '3 Stops',
			key: 'threeStops',
			value: 30,
			loads: 3000,
			revenue: 13500,
			rpm: 4.5,
		},
		{
			name: '4-5 Stops',
			key: 'fourFiveStops',
			value: 20,
			loads: 2000,
			revenue: 9600,
			rpm: 4.8,
		},
		{
			name: '6-7 Stops',
			key: 'sixSevenStops',
			value: 10,
			loads: 1000,
			revenue: 5000,
			rpm: 5.0,
		},
		{
			name: '8-10 Stops',
			key: 'eightTenStops',
			value: 5,
			loads: 500,
			revenue: 2750,
			rpm: 5.5,
		},
	]

	const [tab, setTab] = useState('stopCount')

	const pieData =
		tab === 'teamSolo'
			? teamSolo
			: tab === 'tripDistance'
			? tripDistance
			: stopCount

	// Tooltip similar to PerformanceGauge
	const CustomTooltip = ({ active, payload }) => {
		if (!active || !payload?.length) return null
		const d = payload[0].payload
		const bg = isDarkMode ? '#2d333b' : '#ffffff'
		const txt = isDarkMode ? '#ffffff' : '#111827'
		return (
			<div
				style={{
					backgroundColor: bg,
					borderRadius: '8px',
					padding: '12px',
					boxShadow:
						'0 4px 6px -1px rgba(0,0,0,0.1),0 2px 4px -1px rgba(0,0,0,0.06)',
					color: txt,
				}}
			>
				<div
					style={{ fontWeight: 500, marginBottom: 6, color: getBarFill(d.key) }}
				>
					{d.name}
				</div>
				<div style={{ fontSize: 13 }}>
					<span style={{ fontWeight: 500 }}>Share:</span> {d.value}%
				</div>
				<div style={{ fontSize: 13 }}>
					<span style={{ fontWeight: 500 }}>Loads:</span>{' '}
					{d.loads.toLocaleString()}
				</div>
				<div style={{ fontSize: 13 }}>
					<span style={{ fontWeight: 500 }}>Revenue:</span> $
					{d.revenue.toLocaleString()}
				</div>
				<div style={{ fontSize: 13 }}>
					<span style={{ fontWeight: 500 }}>RPM:</span> ${d.rpm.toFixed(2)}
				</div>
			</div>
		)
	}

	// Helper to format numbers
	const formatNumber = (v, prefix = '') => {
		if (v == null) return '0'
		if (v >= 1000000) return `${prefix}${(v / 1000000).toFixed(1)}M`
		if (v >= 1000) return `${prefix}${(v / 1000).toFixed(1)}K`
		return `${prefix}${v.toFixed(1)}`
	}

	// Custom label like PerformanceGauge
	const renderCustomizedLabel = ({ cx, cy, midAngle, outerRadius, name }) => {
		const slice = pieData.find(p => p.name === name) || {}
		if (slice.value < 2) return null
		const RADIAN = Math.PI / 180
		const radius = outerRadius + 20
		const x = cx + radius * Math.cos(-midAngle * RADIAN)
		const y = cy + radius * Math.sin(-midAngle * RADIAN)

		const percentage = `${Math.round(slice.value)}%`

		return (
			<text
				x={x}
				y={y}
				fill={getBarFill(slice.key)}
				textAnchor={x > cx ? 'start' : 'end'}
				dominantBaseline="central"
				style={{ fontWeight: 600, fontSize: 12 }}
			>
				{name} {percentage}
			</text>
		)
	}

	return (
		<div
			className="rounded-lg p-4"
			style={{ backgroundColor: P.cardBg, border: `1px solid ${P.border}` }}
		>
			{/* Tabs */}
			<div className="flex gap-4 mb-4">
				<button
					onClick={() => setTab('stopCount')}
					className={`text-sm font-medium pb-1 border-b-2 focus:outline-none ${
						tab === 'stopCount'
							? isDarkMode
								? 'border-blue-400 text-blue-300'
								: 'border-blue-600 text-blue-600'
							: 'border-transparent ' +
							  (isDarkMode ? 'text-gray-400' : 'text-gray-500')
					}`}
				>
					Stop&nbsp;Count
				</button>
				<button
					onClick={() => setTab('teamSolo')}
					className={`text-sm font-medium pb-1 border-b-2 focus:outline-none ${
						tab === 'teamSolo'
							? isDarkMode
								? 'border-blue-400 text-blue-300'
								: 'border-blue-600 text-blue-600'
							: 'border-transparent ' +
							  (isDarkMode ? 'text-gray-400' : 'text-gray-500')
					}`}
				>
					Team&nbsp;/&nbsp;Solo
				</button>
				<button
					onClick={() => setTab('tripDistance')}
					className={`text-sm font-medium pb-1 border-b-2 focus:outline-none ${
						tab === 'tripDistance'
							? isDarkMode
								? 'border-blue-400 text-blue-300'
								: 'border-blue-600 text-blue-600'
							: 'border-transparent ' +
							  (isDarkMode ? 'text-gray-400' : 'text-gray-500')
					}`}
				>
					Trip&nbsp;Distance
				</button>
			</div>

			{/* Donut */}
			<div className="flex flex-col items-center">
				<div className="w-full max-w-2xl h-96">
					<ResponsiveContainer width="100%" height={330}>
						<PieChart margin={{ top: 20, right: 30, left: 30, bottom: 20 }}>
							<Pie
								data={pieData}
								innerRadius={115}
								outerRadius={135}
								startAngle={90}
								endAngle={-270}
								dataKey="value"
								label={renderCustomizedLabel}
								labelLine={false}
							>
								{pieData.map(d => (
									<Cell key={d.name} fill={getBarFill(d.key)} />
								))}
								{/* Center text */}
								<text
									x="50%"
									y="50%"
									textAnchor="middle"
									dominantBaseline="central"
									className={`text-sm font-semibold ${
										isDarkMode ? 'fill-white' : 'fill-gray-800'
									}`}
								>
									{pieData.length} Categories
								</text>
							</Pie>
							<Tooltip
								content={<CustomTooltip />}
								wrapperStyle={{ outline: 'none' }}
							/>
						</PieChart>
					</ResponsiveContainer>
				</div>

				{/* Table text breakdown */}
				<div className="w-full grid grid-cols-5 gap-y-2 gap-x-4">
					{/* Header */}
					<div
						className={`text-xs font-semibold ${
							isDarkMode ? 'text-gray-300' : 'text-gray-600'
						}`}
					>
						Type
					</div>
					<div
						className={`text-xs font-semibold ${
							isDarkMode ? 'text-gray-300' : 'text-gray-600'
						}`}
					>
						Share
					</div>
					<div
						className={`text-xs font-semibold ${
							isDarkMode ? 'text-gray-300' : 'text-gray-600'
						}`}
					>
						Loads
					</div>
					<div
						className={`text-xs font-semibold ${
							isDarkMode ? 'text-gray-300' : 'text-gray-600'
						}`}
					>
						Revenue
					</div>
					<div
						className={`text-xs font-semibold ${
							isDarkMode ? 'text-gray-300' : 'text-gray-600'
						}`}
					>
						RPM
					</div>

					{/* Rows */}
					{pieData.map(item => (
						<React.Fragment key={item.name}>
							<div
								className="font-medium text-sm"
								style={{ color: getBarFill(item.key) }}
							>
								{item.name}
							</div>
							<div
								className={`font-medium text-sm ${
									isDarkMode ? 'text-gray-300' : 'text-gray-600'
								}`}
							>
								{item.value}%
							</div>
							<div
								className={`font-medium text-sm ${
									isDarkMode ? 'text-gray-400' : 'text-gray-500'
								}`}
							>
								{formatNumber(item.loads)}
							</div>
							<div
								className={`font-medium text-sm ${
									isDarkMode ? 'text-gray-400' : 'text-gray-500'
								}`}
							>
								{formatNumber(item.revenue, '$')}
							</div>
							<div
								className={`font-medium text-sm ${
									isDarkMode ? 'text-gray-400' : 'text-gray-500'
								}`}
							>
								${item.rpm.toFixed(2)}
							</div>
						</React.Fragment>
					))}
				</div>
			</div>
		</div>
	)
}
